import { ERROR_TEXT } from '@/theme/themes/colors'
import { LocalPinIcon } from '@/components/icons'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { Box, InputAdornment, InputBase, Typography } from '@mui/material'
import { FC, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import useTranslation from '@/hooks/useTranslation'
import { normalizeCoordinate, parseCoordinates } from './coordinates'

const INPUT_WIDTH = '210px'

interface SearchProps {
  onChange?: (coords: { latitude: string; longitude: string }) => void
  value?: string
  className?: string
  colorHolder?: string
  colorText?: string
  sx?: any
  readOnly?: boolean
  /**
   * Told whenever the typed text turns into, or stops being, a coordinate that
   * cannot be used. The parent's Save reads the map marker, which does not
   * move for such text, so without this it would save the old pin as though
   * the typed value had been taken (SCRUM-1032 round 7, item 8).
   */
  onInvalidChange?: (invalid: boolean) => void
}

const CoordinateInput: FC<SearchProps> = ({
  value = '',
  onChange,
  colorText,
  colorHolder,
  className,
  sx,
  readOnly,
  onInvalidChange,
}) => {
  const { t } = useTranslation()
  const themeMode = useSelector(selectTheme)
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const normalizedValue = normalizeCoordinate(inputValue)
    setLocalValue(normalizedValue)
    if (onChange) {
      const coords = parseCoordinates(normalizedValue)
      if (coords) {
        onChange(coords)
      } else if (inputValue === '') {
        onChange({ latitude: '', longitude: '' })
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text')
    const normalizedValue = normalizeCoordinate(pastedData)
    setLocalValue(normalizedValue)
    if (onChange) {
      if (pastedData === '') {
        onChange({ latitude: '', longitude: '' })
        return
      }
      const coords = parseCoordinates(normalizedValue)
      if (coords) onChange(coords)
    }
  }

  // Typed but not a usable coordinate: the marker will not move, so say so.
  const isInvalid =
    !readOnly && localValue.trim() !== '' && !parseCoordinates(localValue)

  // Held in a ref so a parent passing an inline callback does not re-run the
  // effect on every render; only a change in validity is reported.
  const onInvalidChangeRef = useRef(onInvalidChange)
  onInvalidChangeRef.current = onInvalidChange

  useEffect(() => {
    onInvalidChangeRef.current?.(isInvalid)
  }, [isInvalid])

  // An input that goes away while invalid must not leave its parent refusing.
  useEffect(() => () => onInvalidChangeRef.current?.(false), [])

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <InputBase
        className={className}
        value={localValue}
        error={isInvalid}
        placeholder={t('coordinateInputPlaceholder')}
        inputProps={{
          'data-testid': 'coordinate-input',
          'aria-invalid': isInvalid,
        }}
        onChange={handleChange}
        onPaste={handlePaste}
        readOnly={readOnly}
        sx={{
          width: INPUT_WIDTH,
          height: '32px',
          backgroundColor: 'background.default',
          border: {
            ...(themeMode === mode_type.dark
              ? { border: '1px solid #283d43' }
              : { border: '1px solid #D7D7D7' }),
          },
          padding: '8px',
          borderRadius: '5px',
          ...(isInvalid && { outline: '1px solid #dc3545' }),
          boxShadow: '0px 0px 30px rgba(132, 132, 132, 0.25)',
          '& .MuiInputBase-input::placeholder': {
            color: colorHolder || 'text.main',
          },
          [`& .MuiInputBase-input`]: {
            fontSize: '12px',
            fontWeight: '600',
            lineHeight: '100%',
            color: colorText || 'text.main',
          },
          ...sx,
        }}
        startAdornment={
          <InputAdornment position="start" sx={{ color: '#747474' }}>
            <LocalPinIcon sx={{ color: 'text.main' }} />
          </InputAdornment>
        }
      />
      {isInvalid && (
        <Typography
          role="alert"
          data-testid="coordinate-input-error"
          // Anchored under the input and allowed to wrap: the input sits at
          // the right of the map toolbar, and one long nowrap line ran off the
          // side of the map (SCRUM-1032 round 7 follow-up). The panel behind
          // it keeps it readable over the map tiles.
          sx={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 1,
            mt: '4px',
            // No wider than the input, so it stays under it and clear of
            // the hint box to its left on the map toolbar.
            width: INPUT_WIDTH,
            boxSizing: 'border-box',
            px: '8px',
            py: '5px',
            borderRadius: '5px',
            backgroundColor: 'background.default',
            boxShadow: '0px 0px 30px rgba(132, 132, 132, 0.25)',
            fontSize: '11px',
            lineHeight: 1.3,
            // See ERROR_TEXT: `error.main` is near-white in this theme.
            color: ERROR_TEXT[themeMode === mode_type.dark ? 'dark' : 'light'],
            whiteSpace: 'normal',
          }}
        >
          {t('coordinateInputOutOfRange')}
        </Typography>
      )}
    </Box>
  )
}

export default CoordinateInput
